import { Card } from "@amzn/stencil-react-components/card";
import { Chip, ChipSize } from "@amzn/stencil-react-components/chip";
import { Col, GridItem, GridItemProps, Row } from "@amzn/stencil-react-components/layout";
import { LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { Link } from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { AiForMeNews } from "lib/models/aiformenews-models";
import moment from "moment-timezone";
import React, { FC, useMemo } from "react";

const SUMMARY_MAX_WORDS = 15;

/** Strips HTML tags from a string, returning only the text content. */
const stripHtmlTags = (html: string): string => html.replace(/<[^>]*>/g, "");

type GridSpan = {
  lg: GridItemProps["lg"];
  md: GridItemProps["md"];
};

export interface NewsCardProps {
  item: AiForMeNews;
  index: number;
  gridSpan?: GridSpan;
}

export const AiNewsCard: FC<NewsCardProps> = (props: NewsCardProps) => {
  const taxId = new TaxonomyIdItem(props.item.taxonomyId);

  const plainSummary = useMemo(() => stripHtmlTags(props.item.summary ?? ""), [props.item.summary]);
  const summaryWords = useMemo(() => plainSummary.split(/\s+/).filter(Boolean), [plainSummary]);

  const renderSummary = () => {
    if (summaryWords.length <= SUMMARY_MAX_WORDS) {
      return <>{plainSummary}</>;
    }
    const truncated = summaryWords.slice(0, SUMMARY_MAX_WORDS).join(" ").trimEnd();
    return <>{`${truncated}... `}</>;
  };

  return (
    <GridItem
      key={taxId.toString()}
      lg={props.gridSpan?.lg ?? 6}
      md={props.gridSpan?.md ?? 4}
      data-testid="AiForMeItem"
      data-index={props.index}
    >
      <Link
        fontSize="T200"
        color="neutral90"
        textDecoration={LinkTextDecorationOptions.None}
        href={props.item.atozArticleUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "block", width: "100%", height: "100%" }}
        touchpoint="aiforme-landing-news-click"
        customData={{
          title: props.item.title,
          tag: props.item.tag,
          index: props.index,
          url: props.item.atozArticleUrl,
        }}
      >
        <Card style={{ minWidth: "100%", height: "100%" }}>
          <Col gridGap={20} style={{ minWidth: "100%" }}>
            <Row alignItems="center" justifyContent="center">
              <img
                alt={`${props.item.title}`}
                src={props.item.image}
                style={{
                  width: "100%",
                  height: "216px",
                  borderRadius: "8px",
                }}
              />
            </Row>
            <Row alignItems="flex-start" justifyContent="space-between">
              <Chip key={props.item.tag} data-testid={props.item.tag} size={ChipSize.ExtraSmall}>
                {props.item.tag}
              </Chip>
              <span>
                {props.item.catalogicalGenerated?.created.on
                  ? moment(props.item.catalogicalGenerated.created.on).fromNow()
                  : ""}
              </span>
            </Row>
            <Row alignItems="flex-start" justifyContent="space-between">
              <Col>
                <Row alignItems="flex-start">
                  <Text data-testid={`AiForMeItemNewsTitle-${props.item.title}`} fontSize="T300" fontWeight={"bold"}>
                    {props.item.title}
                  </Text>
                </Row>
              </Col>
            </Row>
            <Text
              data-testid={`AiForMeItemDescription-${props.item.title}`}
              color="var(--st-dt-color-text-secondary)"
              style={{ overflow: "auto" }}
            >
              {renderSummary()}
            </Text>
          </Col>
        </Card>
      </Link>
    </GridItem>
  );
};
